import { useEffect, useMemo, useState } from 'react'
import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import {
  getSeparateMiscGuideRows,
  LOGIN_ROW_OPTINS,
  SEPARATE_MISC_SLIP_ROW_IDS,
} from '../../data/stateLoginPortalMap'
import { useOnboarding } from '../../context/OnboardingContext'
import type { StateLoginRow } from '../../types/onboarding'
import { GuideMascotCallout } from '../components/GuideMascotCallout'
import { StateLoginsTable } from '../components/StateLoginsTable'
import { LoginStatesIntro } from '../licenseSteps/LoginStatesIntro'
import { MISC_PRIMARY, MISC_PRIMARY_HOVER } from '../../theme/onboardingTheme'

const primaryButtonSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  backgroundColor: MISC_PRIMARY,
  px: 2.5,
  py: 1.25,
  fontSize: 14,
  fontWeight: 600,
  color: '#ffffff',
  boxShadow: 1,
  textTransform: 'none' as const,
  '&:hover': { backgroundColor: MISC_PRIMARY_HOVER },
}

type GuidePhase =
  | 'optinsIntro'
  | 'optinsLogin'
  | 'optinsSettings'
  | 'optinsCreateUser'
  | 'slipLogin'
  | 'slipUsers'
  | 'slipCreateUser'

function CredentialsTable({ rows }: { rows: StateLoginRow[] }) {
  return (
    <Box sx={{ mx: 'auto', width: '100%', maxWidth: '72rem' }}>
      <StateLoginsTable rows={rows} mode="wizard" readOnly credentialReference />
    </Box>
  )
}

function SlipCreateUserInstructions() {
  return (
    <Paper
      elevation={0}
      sx={{
        mx: 'auto',
        maxWidth: '48rem',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        backgroundColor: 'rgba(248, 250, 252, 0.8)',
        p: 2,
        fontSize: 14,
        lineHeight: 1.625,
        color: '#1e293b',
        boxShadow: 1,
      }}
    >
      <Stack spacing={1}>
        <Typography component="p" sx={{ fontWeight: 600, color: '#0f172a' }}>
          Select <Box component="span" sx={{ fontWeight: 700 }}>Create User</Box> and complete the form with the
          information below:
        </Typography>
        <Box
          component="dl"
          sx={{
            display: 'grid',
            columnGap: 2,
            rowGap: 0.5,
            gridTemplateColumns: { sm: 'max-content 1fr' },
            m: 0,
          }}
        >
          <Typography component="dt" sx={{ fontWeight: 500, color: '#0f172a' }}>
            Contact First Name:
          </Typography>
          <Typography component="dd" sx={{ m: 0 }}>
            Teri
          </Typography>
          <Typography component="dt" sx={{ fontWeight: 500, color: '#0f172a' }}>
            Contact Last Name:
          </Typography>
          <Typography component="dd" sx={{ m: 0 }}>
            Blackshear
          </Typography>
          <Typography component="dt" sx={{ fontWeight: 500, color: '#0f172a' }}>
            Contact Email:
          </Typography>
          <Typography component="dd" sx={{ m: 0 }}>
            MISCFilings@morrisisc.com
          </Typography>
          <Typography component="dt" sx={{ fontWeight: 500, color: '#0f172a' }}>
            Contact Phone:
          </Typography>
          <Typography component="dd" sx={{ m: 0 }}>
            720-502-3980
          </Typography>
          <Typography component="dt" sx={{ fontWeight: 500, color: '#0f172a' }}>
            Suggested Username:
          </Typography>
          <Typography component="dd" sx={{ m: 0 }}>
            MISC_[Your Client Name]
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}

function OptinsCreateUserInstructions() {
  return (
    <Paper
      elevation={0}
      sx={{
        mx: 'auto',
        maxWidth: '48rem',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        backgroundColor: 'rgba(248, 250, 252, 0.8)',
        p: 2,
        fontSize: 14,
        lineHeight: 1.625,
        color: '#1e293b',
        boxShadow: 1,
      }}
    >
      <Stack spacing={1.5}>
        <Typography component="p" sx={{ fontWeight: 600, color: '#0f172a' }}>
          Select &apos;Request New User&apos; and complete the form with the information below:
        </Typography>
        <Box
          component="ul"
          sx={{ listStyle: 'none', m: 0, p: 0, '& li + li': { mt: 0.75 } }}
        >
          <Box component="li">
            <Box component="span" sx={{ fontWeight: 500, color: '#0f172a' }}>
              New User&apos;s First Name:
            </Box>{' '}
            Teri
          </Box>
          <Box component="li">
            <Box component="span" sx={{ fontWeight: 500, color: '#0f172a' }}>
              New User&apos;s Last Name:
            </Box>{' '}
            Blackshear
          </Box>
          <Box component="li">
            <Box component="span" sx={{ fontWeight: 500, color: '#0f172a' }}>
              New User&apos;s Email:
            </Box>{' '}
            Teri.Blackshear@morrisisc.com
          </Box>
          <Box component="li">
            <Box component="span" sx={{ fontWeight: 500, color: '#0f172a' }}>
              New User&apos;s Phone:
            </Box>{' '}
            720-502-3980
          </Box>
        </Box>
        <Typography component="p" sx={{ fontWeight: 500, color: '#0f172a' }}>
          Select User Roles:
        </Typography>
        <Box
          component="ul"
          sx={{
            listStyleType: 'disc',
            m: 0,
            pl: 2.5,
            textAlign: 'left',
            '& li + li': { mt: 0.5 },
          }}
        >
          <Box component="li">Filing Entity Administrator</Box>
          <Box component="li">Industry Filer</Box>
          <Box component="li">
            User Admin Request (the User Admin Request role will allow us to manage any users needed
            for the MISC Team)
          </Box>
        </Box>
        <Typography component="p">Check the attestation box and hit Submit</Typography>
      </Stack>
    </Paper>
  )
}

export function StepSeparateMiscAccountGuide() {
  const { state, dispatch, advance, setStepBackOverride } = useOnboarding()
  const [phase, setPhase] = useState<GuidePhase>('optinsIntro')

  const optinsRows = useMemo(
    () => state.stateLogins.filter((r) => r.stateCode === LOGIN_ROW_OPTINS),
    [state.stateLogins],
  )

  const slipRows = useMemo(
    () =>
      state.stateLogins.filter(
        (r) =>
          SEPARATE_MISC_SLIP_ROW_IDS.includes(
            r.stateCode as (typeof SEPARATE_MISC_SLIP_ROW_IDS)[number],
          ) && !r.pendingLoginWizard,
      ),
    [state.stateLogins],
  )

  const guideRows = useMemo(
    () => getSeparateMiscGuideRows(state.stateLogins, state.activeBusinessStates),
    [state.stateLogins, state.activeBusinessStates],
  )
  const hasSlipRows = slipRows.length > 0

  const phases = useMemo<GuidePhase[]>(
    () => [
      'optinsIntro',
      'optinsLogin',
      'optinsSettings',
      'optinsCreateUser',
      ...(hasSlipRows ? (['slipLogin', 'slipUsers', 'slipCreateUser'] as const) : []),
    ],
    [hasSlipRows],
  )

  const phaseIndex = phases.indexOf(phase)
  const goNext = () => {
    const nextPhase = phases[phaseIndex + 1]
    if (nextPhase) setPhase(nextPhase)
  }
  const acknowledgeGuide = () => {
    dispatch({ type: 'ACK_SEPARATE_MISC_ACCOUNT_GUIDE' })
    advance()
  }

  useEffect(() => {
    const handleGuideBack = () => {
      const currentIndex = phases.indexOf(phase)
      const previousPhase = phases[currentIndex - 1]
      if (!previousPhase) return false
      setPhase(previousPhase)
      return true
    }

    setStepBackOverride(handleGuideBack)
    return () => setStepBackOverride(null)
  }, [phase, phases, setStepBackOverride])

  return (
    <Stack spacing={2.5}>
      <LoginStatesIntro />

      <Box sx={{ mx: 'auto', maxWidth: '56rem', textAlign: 'center' }}>
        <Typography
          component="h2"
          sx={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.025em', color: '#0f172a' }}
        >
          State Logins
        </Typography>
        <Typography component="p" sx={{ mt: 1, fontSize: 14, lineHeight: 1.625, color: '#475569' }}>
          Please enter your credentials for each state filing system below to ensure timely filings on
          your behalf. If you don&apos;t have a login yet, check <strong>No Login</strong> for that
          row — you&apos;ll complete setup on the <strong>New Login Wizard</strong> step next.
        </Typography>
      </Box>

      {phase === 'optinsIntro' ? (
        <>
          <GuideMascotCallout centered>
            <Typography component="p" sx={{ fontWeight: 600, color: '#0f172a' }}>
              These states require a separate login for MISC in order to file. Don&apos;t worry!
              We&apos;ll walk you through the steps to create the logins. If you need more help,
              reach out to us at anytime!
            </Typography>
          </GuideMascotCallout>
          <CredentialsTable rows={guideRows} />
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button type="button" sx={primaryButtonSx} onClick={goNext}>
              I&apos;m ready to get started!
            </Button>
          </Box>
        </>
      ) : null}

      {phase === 'optinsLogin' ? (
        <>
          <GuideMascotCallout centered>
            <Typography component="p">
              Use the credentials you entered on SL Logins below to sign in to{' '}
              <Box component="strong" sx={{ color: '#0f172a' }}>
                OPTins
              </Box>{' '}
              (open the site in another tab when
              you&apos;re ready). I&apos;ll stay right here—click the button once you&apos;re signed
              in.
            </Typography>
          </GuideMascotCallout>
          <Typography
            component="p"
            sx={{ textAlign: 'center', fontSize: '1rem', fontWeight: 600, color: '#0f172a' }}
          >
            Use the credentials displayed below to login to OPTins
          </Typography>
          <CredentialsTable rows={optinsRows} />
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button type="button" sx={primaryButtonSx} onClick={goNext}>
              I&apos;m logged in!
            </Button>
          </Box>
        </>
      ) : null}

      {phase === 'optinsSettings' ? (
        <>
          <GuideMascotCallout centered>
            <Typography component="p">
              Nice work—you&apos;re in! Next, find the{' '}
              <Box component="strong" sx={{ color: '#0f172a' }}>
                Settings
              </Box>{' '}
              tab in OPTins. Let me know when
              you&apos;re there.
            </Typography>
          </GuideMascotCallout>
          <CredentialsTable rows={optinsRows} />
          <Typography
            component="p"
            sx={{ textAlign: 'center', fontSize: '1rem', fontWeight: 600, color: '#0f172a' }}
          >
            Please navigate to the Settings tab
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button type="button" sx={primaryButtonSx} onClick={goNext}>
              I&apos;m in Settings!
            </Button>
          </Box>
        </>
      ) : null}

      {phase === 'optinsCreateUser' ? (
        <>
          <GuideMascotCallout centered>
            <Typography component="p">
              You&apos;re doing great—almost done. Use{' '}
              <Box component="strong" sx={{ color: '#0f172a' }}>
                Request New User
              </Box>{' '}
              and the values below so
              MISC can file for you. Double-check the roles before you submit.
            </Typography>
          </GuideMascotCallout>
          <CredentialsTable rows={optinsRows} />
          <OptinsCreateUserInstructions />
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              type="button"
              sx={primaryButtonSx}
              onClick={hasSlipRows ? goNext : acknowledgeGuide}
            >
              {hasSlipRows
                ? 'Woohoo! I created the OPTins user'
                : 'Woohoo! I created a new user for MISC'}
            </Button>
          </Box>
        </>
      ) : null}

      {phase === 'slipLogin' ? (
        <>
          <GuideMascotCallout centered>
            <Typography component="p">
              Use the credentials for the Administrator Account of the{' '}
              <Box component="strong" sx={{ color: '#0f172a' }}>
                ENTITY
              </Box>{' '}
              (open the site in another tab when
              you&apos;re ready). I&apos;ll stay right here—click the button once you&apos;re signed
              in.
            </Typography>
          </GuideMascotCallout>
          <CredentialsTable rows={slipRows} />
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button type="button" sx={primaryButtonSx} onClick={goNext}>
              I&apos;m logged in!
            </Button>
          </Box>
        </>
      ) : null}

      {phase === 'slipUsers' ? (
        <>
          <GuideMascotCallout centered>
            <Typography component="p">
              Nice work—you&apos;re in! Next, find the{' '}
              <Box component="strong" sx={{ color: '#0f172a' }}>
                User
              </Box>{' '}
              tab in SLIP. Let me know when
              you&apos;re there.
            </Typography>
          </GuideMascotCallout>
          <CredentialsTable rows={slipRows} />
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button type="button" sx={primaryButtonSx} onClick={goNext}>
              I&apos;m in Users!
            </Button>
          </Box>
        </>
      ) : null}

      {phase === 'slipCreateUser' ? (
        <>
          <GuideMascotCallout centered>
            <Typography component="p">
              You&apos;re doing great—almost done. Use{' '}
              <Box component="strong" sx={{ color: '#0f172a' }}>
                Create User
              </Box>{' '}
              and the values below so MISC
              can file for you.
            </Typography>
          </GuideMascotCallout>
          <CredentialsTable rows={slipRows} />
          <SlipCreateUserInstructions />
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button type="button" sx={primaryButtonSx} onClick={acknowledgeGuide}>
              Woohoo! I created the SLIP user for MISC
            </Button>
          </Box>
        </>
      ) : null}
    </Stack>
  )
}
